<script setup>
import { useFilterSidePanel, VisionAiAnimation } from '@dashhudson/dashing-ui';
import { computed, useTemplateRef, watch, ref, onMounted, onUnmounted } from 'vue';
import { useVisionAiStore } from '@/stores/vision-ai';
import { useHubspotStore } from '@/stores/hubspot';
import { aiSummaryLaunchEvent } from '@/components/VisionAi/utils/mixpanel-events';
import { VISION_AI_SUMMARY_LAUNCH_TYPE } from '@/components/VisionAi/constants';
import { useVisionModelUpgrade } from '@/components/VisionAi/composables/useVisionModelUpgrade';

const visionAiStore = useVisionAiStore();
const emit = defineEmits(['openChatWindow']);

const props = defineProps({
  active: {
    type: Boolean,
    default: false,
  },
});

const animateVisionAiAnimation = ref(false);
const expand = ref(false);
const statusMessage = ref(null);

const {
  shouldShowUpgradeNotification,
  shouldShowUpgradeMessage,
  currentStatusMessage,
  markUpgradeDetected,
  trackVisionModelUpdated,
} = useVisionModelUpgrade();

let notificationTimeout = null;

function startVisionNotification() {
  animateVisionAiAnimation.value = true;
  statusMessage.value = currentStatusMessage.value;
  markUpgradeDetected();
}

function handleGlobalClick() {
  if (!shouldShowUpgradeNotification()) {
    return;
  }

  if (notificationTimeout) {
    clearTimeout(notificationTimeout);
  }

  notificationTimeout = setTimeout(() => {
    startVisionNotification();
    notificationTimeout = null;
  }, 3000);
}

onMounted(() => {
  document.addEventListener('click', handleGlobalClick);

  if (shouldShowUpgradeMessage()) {
    startVisionNotification();
  }
});

onUnmounted(() => {
  document.removeEventListener('click', handleGlobalClick);
  if (notificationTimeout) {
    clearTimeout(notificationTimeout);
  }
});

const { visible: filterVisible } = useFilterSidePanel();
const hubspotStore = useHubspotStore();

const globalRight = computed(() => {
  if (filterVisible.value) {
    if (hubspotStore.ready) {
      if (hubspotStore.isGdsVersion) {
        return '!right-[24.75rem]';
      }
      return '!right-[26.50rem]';
    }
    return '!right-[22rem]';
  }

  if (hubspotStore.ready) {
    if (hubspotStore.isGdsVersion) {
      return '!right-[2.75rem]';
    }
    return '!right-[4.50rem]';
  }
  return '!right-0';
});

const buttonPositionClasses = computed(() => `${globalRight.value} !fixed !bottom-4 !mr-4`);

function clearVisionNotification() {
  expand.value = false;
  animateVisionAiAnimation.value = false;
  statusMessage.value = null;
  trackVisionModelUpdated('notification_cleared');
}

async function handleButtonClicked(event) {
  visionAiStore.openChatWindow();
  clearVisionNotification();
  emit('openChatWindow', event);
  aiSummaryLaunchEvent({
    launchedFrom: VISION_AI_SUMMARY_LAUNCH_TYPE.ICON,
    intentType: visionAiStore.currentIntentType,
  });
}

const expanded = computed(() => expand.value || props.active);
const visionAiAnimation = useTemplateRef('visionAiAnimationRef');

watch(
  () => expanded.value,
  (newVal) => {
    if (newVal && visionAiAnimation.value) {
      visionAiAnimation.value.resetAnimation();
    }
  },
);

function handleMouseEnter() {
  if (statusMessage.value) {
    expand.value = true;
  }
}
</script>

<template>
  <div
    data-cy="vision-ai-button-global"
    :class="['vision-ai-button dash-relative !pointer-events-auto', buttonPositionClasses]"
    :style="{ zIndex: '9999' }"
  >
    <div
      :class="[
        'dash-bg-vision-ai',
        'dash-rounded-full',
        'dash-py-2',
        'dash-pl-2',
        'dash-cursor-pointer',
        'dash-overflow-hidden',
        'dash-transition-lg',
        { 'dash-pr-2 !max-w-10': !expanded, 'dash-pr-4 !max-w-[350px]': expanded },
      ]"
      @mouseenter="handleMouseEnter"
      @click="handleButtonClicked($event)"
    >
      <div
        :class="[
          'dash-flex dash-overflow-hidden',
          { 'dash-gap-2': expanded, 'dash-justify-center': !expanded },
        ]"
      >
        <VisionAiAnimation
          ref="visionAiAnimationRef"
          exponential-delay
          :disabled="!animateVisionAiAnimation"
        />
        <Transition name="fade">
          <div v-if="expanded" class="dash-text-nowrap">
            <span class="dash-typo-base-semibold dash-text-knockout">{{ statusMessage }}</span>
          </div>
        </Transition>
      </div>
    </div>
  </div>
</template>
